import { useState, useEffect } from 'react';
/** Wall-clock in `tz` → UTC instant. Read the naive value as UTC, ask Intl what
 *  that instant looks like in the zone, subtract the difference. */
function wallClockToUTC(date: string, time: string, tz: string): string {
    const naive = new Date(`${date}T${time}:00Z`);
    const p = Object.fromEntries(
        new Intl.DateTimeFormat('en-US', {
            timeZone: tz, hour12: false,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        }).formatToParts(naive).map(x => [x.type, x.value])
    );
    const asZoned = Date.UTC(
        Number(p.year), Number(p.month) - 1, Number(p.day),
        Number(p.hour) % 24, Number(p.minute), Number(p.second)
    );
    return new Date(naive.getTime() - (asZoned - naive.getTime()))
        .toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** UTC instant → wall-clock fields for the date/time inputs. */
function utcToWallClock(iso: string, tz: string): { date: string; time: string } {
    const p = Object.fromEntries(
        new Intl.DateTimeFormat('en-US', {
            timeZone: tz, hour12: false,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit',
        }).formatToParts(new Date(iso)).map(x => [x.type, x.value])
    );
    return {
        date: `${p.year}-${p.month}-${p.day}`,
        time: `${String(Number(p.hour) % 24).padStart(2, '0')}:${p.minute}`,
    };
}

export function PreviewBar() {
    const [open, setOpen] = useState(false);
    // Cookie and location reads are client-only, so the server renders nothing.
    // Rendering nothing on the first client pass too keeps hydration in sync.
    const [mounted, setMounted] = useState(false);
    const [previewTz, setPreviewTz] = useState('Etc/UTC');

    // Merchant's configured zone, which must match the BM instance time zone.
    useEffect(() => {
        const cfg = (window as any).__APP_CONFIG__?.cms;
        if (!cfg?.url || !cfg?.storefront_id) return;
        fetch(`${cfg.url}/api/storefronts/${cfg.storefront_id}/public/timezone`)
            .then(r => (r.ok ? r.json() : null))
            .then(d => { if (d?.timezone) setPreviewTz(d.timezone); })
            .catch(() => {});
    }, []);

    
    useEffect(() => {
        const p = new URLSearchParams(window.location.search);
        if (p.get('preview_ok') === '1') {
            document.cookie = 'ca_preview_ok=1; Path=/; SameSite=Lax; Max-Age=28800';
            p.delete('preview_ok');
            window.history.replaceState({}, '', window.location.pathname + (p.toString() ? `?${p}` : ''));
        }
        setMounted(true);
    }, []);

    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    const cookiePreview = typeof document !== 'undefined'
        ? document.cookie.match(/(?:^|;\s*)ca_preview_date=([^;]*)/)?.[1]
        : undefined;
    // Cookie is the source of truth for bar state — the URL param is transient and is
    // also used to carry the reset date on exit, which must not re-show the bar.
    const currentPreview = cookiePreview ? decodeURIComponent(cookiePreview) : null;
    const [date, setDate] = useState(currentPreview?.split('T')[0] || '');
    const [time, setTime] = useState(currentPreview?.split('T')[1]?.replace('Z', '').substring(0, 5) || '12:00');

    // Today's date for min attribute
    const today = new Date().toISOString().split('T')[0];

    // Re-render the inputs whenever the zone resolves or the preview date changes.
    useEffect(() => {
        if (!currentPreview) return;
        // A stale or malformed cookie value yields Invalid Date, and formatToParts
        // throws on that — which would take down the whole render.
        if (Number.isNaN(new Date(currentPreview).getTime())) return;
        try {
            const wc = utcToWallClock(currentPreview, previewTz);
            setDate(wc.date);
            setTime(wc.time);
        } catch {}
    }, [currentPreview, previewTz]);


    if (typeof window === 'undefined' || !mounted) return null;
    // Staging enables Preview Future outright — it's the UAT environment and the
    // merchandiser shouldn't need a token to do their job. Production leaves the
    // flag off, so the button only appears for an authorized preview session.
    const previewOpen = (window as any).__APP_CONFIG__?.features?.previewFuture?.enabled === true;
    const hasPreviewAuth = previewOpen || document.cookie.includes('ca_preview_ok=');
    if (!hasPreviewAuth) return null;

    function activatePreview() {
        if (!date) return;
        // SCAPI effectiveDateTime rejects fractional seconds and offset form — seconds-precision UTC only.
        const previewDate = wallClockToUTC(date, time, previewTz);
        // 6h to match the shopper-context TTL.
        document.cookie = `ca_preview_date=${encodeURIComponent(previewDate)}; path=/; max-age=21600`;
        const url = new URL(window.location.href);
        url.searchParams.set('preview_date', previewDate);
        window.location.href = url.toString();
    }

    function exitPreview() {
        // Reset through a navigation, not a fetcher: the URL qualifier takes precedence
        // over stale cookie state in the middleware merge, so context lands deterministically.
        // A fetcher races the reload and the old future date gets re-PUT.
        document.cookie = 'ca_preview_date=; path=/; max-age=0';
        const now = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
        const url = new URL(window.location.href);
        url.searchParams.set('preview_date', now);
        window.location.href = url.toString();
    }

    // Active preview mode — green bar at top
    if (currentPreview) {
        return (
            <div style={{
                position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
                background: '#16a34a', color: '#fff', padding: '10px 20px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
                fontSize: 13, fontWeight: 600, fontFamily: '-apple-system, sans-serif',
                boxShadow: '0 -2px 8px rgba(0,0,0,0.2)',
            }}>
                <span>🕐 Previewing: {new Date(currentPreview).toLocaleString('en-US', { timeZone: previewTz })} ({previewTz})</span>
                <button
                    onClick={() => setOpen(!open)}
                    style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                >
                    Change Date
                </button>
                <button
                    onClick={exitPreview}
                    style={{ background: '#fff', border: 'none', color: '#16a34a', padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                >
                    Return to Present
                </button>
                {open && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} style={{ padding: '4px 8px', borderRadius: 4, border: 'none', fontSize: 12 }} />
                        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={{ padding: '4px 8px', borderRadius: 4, border: 'none', fontSize: 12 }} />
                        <button onClick={activatePreview} style={{ background: '#fff', border: 'none', color: '#16a34a', padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Go</button>
                    </div>
                )}
            </div>
        );
    }

    

    // Default state — purple button bottom-right
    return (
        <div style={{
            position: 'fixed', bottom: 20, left: 20, zIndex: 9999,
        }}>
            <button
                onClick={() => setOpen(!open)}
                style={{
                    background: '#7c3aed', color: '#fff', border: 'none',
                    padding: '10px 16px', borderRadius: 8, cursor: 'pointer',
                    fontSize: 13, fontWeight: 600, fontFamily: '-apple-system, sans-serif',
                    boxShadow: '0 2px 12px rgba(124,58,237,0.4)',
                    display: 'flex', alignItems: 'center', gap: 8,
                }}
            >
                🕐 Preview Future
            </button>
            {open && (
                <div style={{
                    position: 'absolute', bottom: 50, left: 0,
                    background: '#fff', borderRadius: 12, padding: 16,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)', width: 260,
                }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111', marginBottom: 12 }}>Preview Future Date</div>
                    <div style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Date</label>
                        <input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' as const }} />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>Time</label>
                        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' as const }} />
                    </div>
                    <button
                        onClick={activatePreview}
                        disabled={!date}
                        style={{ width: '100%', padding: '8px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: date ? 1 : 0.5 }}
                    >
                        Preview
                    </button>
                </div>
            )}
        </div>
    );
}
